/**
 * RECORD_FIELDS.js
 * Definición de campos para registros de mascota y llavero
 * 
 * TIPOS SOPORTADOS:
 * - mascota: Tags para perros, gatos y otras mascotas
 * - llavero: Tags para objetos (llaves, mochilas, etc.)
 */

export const RECORD_FIELDS = {
  // ══════════════════════════════════════════════════════════
  // CAMPOS ADMINISTRATIVOS
  // ══════════════════════════════════════════════════════════
  id: {
    label: "ID (código de Registro)",
    labelEn: "ID (Record ID)",
    section: "Administrativos",
    default: "",
    inputType: "readonly",
    visibleIn: ["adminEdicion", "adminList"],
    orderList: 1,
    applicableTypes: ["mascota", "llavero"],
  },
  
  codigoAcceso: {
    label: "Código de Acceso",
    labelEn: "Access Code",
    section: "Administrativos",
    default: "",
    inputType: "readonly",
    visibleIn: ["adminEdicion", "adminList"],
    orderList: 2,
    applicableTypes: ["mascota", "llavero"],
  },
  
  createdAt: {
    label: "Fecha de Creación",
    labelEn: "Creation Date",
    section: "Administrativos",
    default: "",
    inputType: "readonly",
    visibleIn: ["adminEdicion", "adminList"],
    orderList: 3,
    applicableTypes: ["mascota", "llavero"],
  },
  
  fechaActivacion: {
    label: "Fecha de Activación",
    labelEn: "Activation Date",
    section: "Administrativos",
    default: "",
    inputType: "readonly",
    visibleIn: ["adminEdicion", "adminList", "consultar"],
    orderList: 4,
    applicableTypes: ["mascota", "llavero"],
  },
  
  estadoImpresion: {
    label: "Estado de Impresión",
    labelEn: "Printing Status",
    section: "Administrativos",
    default: "pendiente",
    inputType: "select",
    options: ["pendiente", "impreso", "danado", "entregado"],
    visibleIn: ["adminEdicion"],
    orderList: 5,
    applicableTypes: ["mascota", "llavero"],
  },
  
  folio: {
    label: "Folio (Consecutivo)",
    labelEn: "Folio (Consecutive)",
    section: "Administrativos",
    default: "",
    inputType: "readonly",
    visibleIn: ["adminEdicion", "adminList"],
    orderList: 6,
    applicableTypes: ["mascota", "llavero"],
  },
  
  estado: {
    label: "Estado",
    labelEn: "Status",
    section: "Administrativos",
    default: "inactivo",
    inputType: "select",
    options: ["inactivo", "activo", "vencido", "pendientePorLigar", "pendienteVerificacion"],
    visibleIn: ["adminEdicion", "adminList"],
    orderList: 7,
    applicableTypes: ["mascota", "llavero"],
  },
  
  plan: {
    label: "Plan",
    labelEn: "Plan",
    section: "Administrativos",
    default: "premium",
    inputType: "select",
    options: ["light","standard", "premium"],
    visibleIn: ["adminCreacion", "adminEdicion"],
    orderList: 8,
    applicableTypes: ["mascota", "llavero"],
  },
  
  duracionPlanMeses: {
    label: "Duración (meses)",
    labelEn: "Duration (months)",
    section: "Administrativos",
    default: 12,
    inputType: "number",
    visibleIn: ["adminCreacion", "adminEdicion"],
    orderList: 9,
    applicableTypes: ["mascota", "llavero"],
  },
  
  fechaVencimiento: {
    label: "Fecha de Vencimiento",
    labelEn: "Expiration Date",
    section: "Administrativos",
    default: null,
    inputType: "readonly",
    visibleIn: ["adminEdicion", "adminList"],
    orderList: 10,
    applicableTypes: ["mascota", "llavero"],
  },
  
  planActivo: {
    label: "Plan Activo",
    labelEn: "Active Plan",
    section: "Administrativos",
    default: false,
    inputType: "checkbox",
    visibleIn: ["adminEdicion"],
    orderList: 11,
    applicableTypes: ["mascota", "llavero"],
  },
  
  tipo: {
    label: "Tipo de Registro",
    labelEn: "Record Type",
    section: "Administrativos",
    default: "",
    inputType: "select",
    options: ["mascota", "llavero"],
    visibleIn: ["adminCreacion", "adminList"],
    orderList: 12,
    applicableTypes: ["mascota", "llavero"],
  },
  
  codigoHabilitado: {
    label: "Código Habilitado",
    labelEn: "Code Enabled",
    section: "Administrativos",
    default: true,
    inputType: "checkbox",
    visibleIn: ["adminCreacion", "adminEdicion"],
    orderList: 13,
    applicableTypes: ["mascota", "llavero"],
  },

  // ══════════════════════════════════════════════════════════
  // CAMPOS DE ACTIVACIÓN
  // ══════════════════════════════════════════════════════════
  cuentaExistente: {
    label: "Ya tengo una cuenta en LemmonTags",
    labelEn: "I already have an account",
    section: "Activación",
    default: false,
    inputType: "checkbox",
    orderList: 1,
    visibleIn: ["activar", "ligandoSiguiente"],
    applicableTypes: ["mascota", "llavero", "coleccionable"],
    helperText: "Selecciona esta opción si ya tienes otros tags registrados"
  },

  // ══════════════════════════════════════════════════════════
  // CAMPOS GENERALES (Aplican a todos los tipos)
  // ══════════════════════════════════════════════════════════
  nombre: {
    label: "Nombre",
    labelEn: "Name",
    section: "Información General",
    validation: ["required"],
    default: "",
    placeholder: "Ingrese el nombre",
    placeholderEn: "Enter name",
    visibleIn: ["adminCreacion", "adminEdicion", "consultar", "activar", "userEdicion"],
    orderList: 20,
    applicableTypes: ["llavero", "mascota", "coleccionable"],
    
    // Labels y placeholders dinámicos según el tipo
    getDynamicLabel: (tipo) => {
      switch(tipo) {
        case 'mascota':
          return { 
            label: "Nombre de tu Mascota", 
            labelEn: "Pet Name",
            helperText: "¿Cómo se llama tu compañero peludo?"
          };
        case 'llavero':
          return { 
            label: "Nombre Identificador", 
            labelEn: "Identifier Name",
            helperText: "Dale un nombre para identificar este tag. Ej: 'Llaves de Casa', 'Mochila Escolar'"
          };
        default:
          return { label: "Nombre", labelEn: "Name" };
      }
    },
    
    getDynamicPlaceholder: (tipo) => {
      switch(tipo) {
        case 'mascota':
          return { 
            placeholder: "Ej: Rocky, Luna, Max...", 
            placeholderEn: "E.g: Rocky, Luna, Max..." 
          };
        case 'llavero':
          return { 
            placeholder: "Ej: Llaves del Auto, Mochila de Viaje...", 
            placeholderEn: "E.g: Car Keys, Travel Backpack..." 
          };
        default:
          return { 
            placeholder: "Ingrese el nombre", 
            placeholderEn: "Enter name" 
          };
      }
    }
  },
  
  fotoFile: {
    label: "Foto",
    labelEn: "Photo",
    section: "Información General",
    default: "",
    inputType: "file",
    visibleIn: ["adminCreacion", "adminEdicion", "consultar", "userEdicion", "consultaDelVeterinario"],
    orderList: 21,
    applicableTypes: ["mascota", "llavero", "coleccionable"],
    helperText: "Foto para ayudar a identificar"
  },
  
  extraviado: {
    label: "¿Extraviado?",
    labelEn: "Lost?",
    section: "Estado de Pérdida",
    default: false,
    inputType: "checkbox",
    visibleIn: ["adminCreacion", "consultar"],
    orderList: 30,
    applicableTypes: ["mascota", "llavero"],
  },
  
  ubicacionPerdida: {
    label: "Zona/Lugar de Pérdida",
    labelEn: "Lost Location/Zone",
    section: "Estado de Pérdida",
    default: "",
    placeholder: "Ej: Parque Central, Col. Roma Norte",
    placeholderEn: "E.g: Central Park, Downtown",
    visibleIn: ["adminCreacion", "lostItem"],
    orderList: 31,
    applicableTypes: ["mascota", "llavero"],
    helperText: "Zona aproximada donde se perdió"
  },
  
  recompensa: {
    label: "Recompensa Ofrecida",
    labelEn: "Reward",
    section: "Estado de Pérdida",
    default: "",
    placeholder: "Ej: $500 pesos, tarjeta de regalo",
    placeholderEn: "E.g: $50 USD, gift card",
    visibleIn: ["adminCreacion", "consultar", "lostItem"],
    orderList: 32,
    applicableTypes: ["mascota", "llavero"],
    helperText: "Solo disponible para plan Premium"
  },

  // ══════════════════════════════════════════════════════════
  // CAMPOS ESPECÍFICOS DE MASCOTA
  // ══════════════════════════════════════════════════════════
  especie: {
    label: "Tipo de Mascota",
    labelEn: "Species",
    inputType: "select",
    options: ["Perro", "Gato", "Otro"],
    section: "Información de Mascota",
    default: "Perro",
    placeholder: "-- Seleccione --",
    placeholderEn: "-- Select --",
    validation: [],
    visibleIn: [
      "adminCreacion",
      "adminEdicion", 
      "consultar",
      "userEdicion",
      "consultaDelVeterinario",
    ],
    orderList: 40,
    applicableTypes: ["mascota"],
    helperText: "Puedes completar esto después si no estás seguro"
  },
  
  raza: {
    label: "Raza",
    labelEn: "Breed",
    section: "Información de Mascota",
    validation: [],
    default: "",
    placeholder: "Ej: Labrador, Chihuahua, Mestizo...",
    placeholderEn: "E.g: Labrador, Chihuahua, Mixed...",
    visibleIn: [
      "adminCreacion", 
      "adminEdicion", 
      "consultar", 
      "userEdicion", 
      "consultaDelVeterinario"
    ],
    orderList: 41,
    applicableTypes: ["mascota"],
    helperText: "Opcional - Puedes completarlo después desde tu panel"
  },
  
  fechaNacimiento: {
    label: "Fecha de Nacimiento",
    labelEn: "Birth Date",
    section: "Información de Mascota",
    default: "",
    inputType: "date",
    visibleIn: [
      "adminCreacion",
      "adminEdicion",
      "consultar",
      "userEdicion",
      "consultaDelVeterinario",
    ],
    orderList: 42,
    applicableTypes: ["mascota"],
    helperText: "Opcional - Ayuda a calcular la edad de tu mascota"
  },
  
  senasParticulares: {
    label: "Señas Particulares",
    labelEn: "Distinctive Marks",
    section: "Información de Mascota",
    default: "",
    placeholder: "Ej: Mancha blanca en pata trasera, collar azul...",
    placeholderEn: "E.g: White spot on rear paw, blue collar...",
    visibleIn: [
      "adminCreacion",
      "adminEdicion",
      "consultar",
      "consultaDelVeterinario",
    ],
    orderList: 43,
    applicableTypes: ["mascota"],
    helperText: "Este campo se completa automáticamente con Foto-ID"
  },
  
  alergias: {
    label: "Alergias",
    labelEn: "Allergies",
    section: "Información de Mascota",
    default: "",
    placeholder: "Ej: Polen, ciertos medicamentos, alimentos...",
    placeholderEn: "E.g: Pollen, certain medications, foods...",
    visibleIn: [
      "adminCreacion",
      "adminEdicion",
      "consultar",
      "userEdicion",
      "consultaDelVeterinario",
    ],
    orderList: 44,
    applicableTypes: ["mascota"],
    helperText: "Importante para veterinarios en caso de emergencia"
  },

  comportamientoConExtranos: {
    label: "Comportamiento con extraños",
    labelEn: "Behavior with strangers",
    section: "Información de Mascota",
    default: "",
    inputType: "textarea",
    placeholder: "Ej: Muy miedosa, no se deja tocar / Sociable, se acerca a cualquiera / Ladra pero no muerde",
    placeholderEn: "E.g: Very shy, won't let strangers touch / Friendly, approaches anyone / Barks but doesn't bite",
    visibleIn: [
      "adminCreacion",
      "adminEdicion",
      "consultar",
      "userEdicion",
      "consultaDelVeterinario",
    ],
    orderList: 45,
    applicableTypes: ["mascota"],
    helperText: "Útil para quien encuentre a tu mascota"
  },

  configuracionRecordatorio: {
    label: "Configuración de Recordatorio",
    labelEn: "Reminder Settings",
    section: "Recordatorios",
    default: {
      activo: false,
      hora: "09:00",
      diasSemana: ["L", "M", "X", "J", "V", "S", "D"]
    },
    inputType: "none",
    visibleIn: [],
    orderList: 50,
    applicableTypes: ["mascota"],
  },
  
  veterinarios: {
    label: "Veterinarios Asociados",
    labelEn: "Associated Veterinarians",
    section: "Información de Mascota",
    default: [],
    inputType: "none",
    visibleIn: ["adminEdicion"],
    orderList: 51,
    applicableTypes: ["mascota"],
  },

  // ══════════════════════════════════════════════════════════
  // CAMPOS ESPECÍFICOS DE LLAVERO
  // ══════════════════════════════════════════════════════════
  descripcion: {
    label: "Descripción",
    labelEn: "Description",
    section: "Información del Objeto",
    default: "",
    validation: [],
    placeholder: "Ej: Mochila roja con cremallera negra...",
    placeholderEn: "E.g: Red backpack with black zipper...",
    visibleIn: ["adminCreacion", "adminEdicion", "consultar", "userEdicion"],
    orderList: 60,
    applicableTypes: ["llavero", "coleccionable"],
    helperText: "Opcional - Puedes completar esto después desde tu panel"
  },
  
  descripcionEditable: {
    label: "¿Descripción Editable?",
    labelEn: "Editable Description?",
    section: "Información del Objeto",
    default: false,
    inputType: "checkbox",
    visibleIn: ["adminCreacion", "adminEdicion"],
    orderList: 61,
    applicableTypes: ["llavero"],
  },
  
  coleccion: {
    label: "Colección",
    labelEn: "Collection",
    section: "Información del Objeto",
    default: "",
    visibleIn: ["adminCreacion", "adminEdicion", "consultar"],
    inputType: "readonly",
    orderList: 62,
    applicableTypes: ["llavero"],
  },
  
  numeroPieza: {
    label: "Número de Pieza",
    labelEn: "Piece Number",
    section: "Información del Objeto",
    default: "",
    visibleIn: ["adminCreacion", "adminEdicion", "consultar"],
    inputType: "readonly",
    orderList: 63,
    applicableTypes: ["llavero"],
  },
  
  totalPiezas: {
    label: "Total de Piezas",
    labelEn: "Total Pieces",
    section: "Información del Objeto",
    default: "",
    visibleIn: ["adminCreacion", "adminEdicion", "consultar"],
    inputType: "readonly",
    orderList: 64,
    applicableTypes: ["llavero"],
  },

  // ══════════════════════════════════════════════════════════
  // CAMPOS PERSONALIZADOS (Premium)
  // ══════════════════════════════════════════════════════════
  customFieldLabel: {
    label: "Etiqueta Personalizada",
    labelEn: "Custom Field Label",
    section: "Campos Personalizados",
    default: "",
    visibleIn: ["adminCreacion", "adminEdicion"],
    orderList: 70,
    applicableTypes: ["mascota", "llavero"],
  },
  
  customFieldValue: {
    label: "Valor Campo Personalizado",
    labelEn: "Custom Field Value",
    section: "Campos Personalizados",
    default: "",
    visibleIn: ["adminCreacion", "adminEdicion"],
    orderList: 71,
    applicableTypes: ["mascota", "llavero"],
  },
};